import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart3, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMOJI_MAP = {
  like: "👍", love: "❤️", haha: "😂", wow: "😮", sad: "😢", angry: "😡", care: "🤗",
};

export default function ReactionsAnalyticsCard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reactions/analytics?days=${d}`);
      setData(r.data);
    } catch { /* silent */ }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(days); }, [days]);
  const totals = data?.totals || {};
  const sortedTotals = Object.entries(totals).sort((a, b) => b[1] - a[1]);
  const maxCount = sortedTotals.length ? sortedTotals[0][1] : 1;

  // Group daily by date (each date has multiple emoji lines)
  const byDate = {};
  (data?.daily || []).forEach((row) => {
    (byDate[row.date] = byDate[row.date] || {})[row.emoji] = row.count;
  });
  const dates = Object.keys(byDate).sort();

  return (
    <Card className="p-5 space-y-4" data-testid="reactions-analytics-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" /> Reações — Analytics
          </h2>
          <p className="text-xs text-muted-foreground">Emojis mais usados em posts e comentários</p>
        </div>
        <div className="flex gap-1">
          {[7, 14, 30].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? "default" : "outline"}
              onClick={() => setDays(d)}
              data-testid={`analytics-range-${d}`}
              className="rounded-full h-8 text-xs"
            >{d}d</Button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Carregando...</div>}

      {data && !loading && (
        <>
          <div className="grid sm:grid-cols-3 gap-3">
            <Card className="p-3 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="text-[10px] uppercase text-muted-foreground font-bold tracking-wider">Total</div>
              <div className="text-3xl font-bold" data-testid="analytics-grand-total">{data.grand_total.toLocaleString("pt-BR")}</div>
              <div className="text-xs text-muted-foreground">reações nos últimos {data.days} dias</div>
            </Card>
            <Card className="p-3 bg-gradient-to-br from-yellow-500/10 to-transparent">
              <div className="text-[10px] uppercase text-muted-foreground font-bold tracking-wider flex items-center gap-1">
                <Trophy className="w-3 h-3" /> Emoji Rei
              </div>
              <div className="text-4xl" data-testid="analytics-top-emoji">
                {data.top_emoji ? EMOJI_MAP[data.top_emoji] : "—"}
              </div>
              <div className="text-xs text-muted-foreground capitalize">{data.top_emoji || "sem dados"}</div>
            </Card>
            <Card className="p-3 bg-gradient-to-br from-emerald-500/10 to-transparent">
              <div className="text-[10px] uppercase text-muted-foreground font-bold tracking-wider">Média/dia</div>
              <div className="text-3xl font-bold">
                {data.days ? Math.round(data.grand_total / data.days).toLocaleString("pt-BR") : 0}
              </div>
              <div className="text-xs text-muted-foreground">reações por dia</div>
            </Card>
          </div>

          {/* Emoji ranking with bars */}
          <div className="space-y-1.5">
            <div className="text-xs font-semibold text-muted-foreground">Ranking geral</div>
            {sortedTotals.length === 0 && (
              <div className="text-sm text-muted-foreground italic">Ainda sem reações no período.</div>
            )}
            {sortedTotals.map(([emoji, count]) => (
              <div key={emoji} className="flex items-center gap-3" data-testid={`analytics-row-${emoji}`}>
                <div className="text-2xl w-8 text-center">{EMOJI_MAP[emoji] || emoji}</div>
                <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-emerald-500 rounded-full transition-all"
                    style={{ width: `${(count / maxCount) * 100}%` }}
                  />
                </div>
                <div className="text-sm font-bold w-16 text-right">{count.toLocaleString("pt-BR")}</div>
              </div>
            ))}
          </div>

          {/* Daily heatmap */}
          {dates.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs font-semibold text-muted-foreground">Últimos {dates.length} dias</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left py-1 pr-2 font-semibold text-muted-foreground">Data</th>
                      {Object.keys(EMOJI_MAP).map((e) => (
                        <th key={e} className="text-center py-1 px-1 font-normal">{EMOJI_MAP[e]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dates.slice(-14).map((date) => (
                      <tr key={date} className="border-t border-border/40">
                        <td className="py-1 pr-2 text-muted-foreground whitespace-nowrap">
                          {new Date(date).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
                        </td>
                        {Object.keys(EMOJI_MAP).map((e) => {
                          const c = byDate[date]?.[e] || 0;
                          return (
                            <td key={e} className="text-center py-1 px-1">
                              {c > 0 ? <span className="inline-block px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold">{c}</span> : <span className="opacity-30">·</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
